/**
 * AudioWaveform Component
 * Displays an animated audio waveform visualization for voice mode
 * 
 * Libraries recommended for advanced visualizations:
 * 1. **wavesurfer.js** - Full-featured waveform with zoom, playback controls
 *    npm install wavesurfer.js
 *    Pros: Rich features, highly customizable
 *    Cons: Heavier bundle size (~100KB)
 * 
 * 2. **react-audio-visualize** - React-specific, lightweight
 *    npm install react-audio-visualize
 *    Pros: React hooks, easy to use
 *    Cons: Limited customization
 * 
 * 3. **react-mic** - Real-time microphone visualization
 *    npm install react-mic
 *    Pros: Built for live audio, simple API
 *    Cons: Less maintained
 * 
 * 4. **Web Audio API + Canvas** - Native (no dependencies)
 *    Pros: Zero dependencies, full control
 *    Cons: More code to write
 * 
 * This component uses pure CSS/SVG for minimal bundle impact.
 * For production, consider wavesurfer.js or react-audio-visualize.
 */

import React, { useEffect, useRef, useState } from 'react';

interface AudioWaveformProps {
  isActive: boolean;
  isPaused?: boolean; // New prop to show dots instead of waves
  audioLevel?: number; // 0.0 to 1.0 (kept for backward compatibility)
  frequencyData?: Uint8Array; // Frequency spectrum data (0-255 per bin)
  size?: number;
  color?: string;
}

export const AudioWaveform: React.FC<AudioWaveformProps> = ({ 
  isActive, 
  isPaused = false,
  audioLevel = 0,
  frequencyData,
  size = 24,
  color = 'currentColor'
}) => {
  const [bars, setBars] = useState<number[]>([0.2, 0.4, 0.6, 0.4, 0.2]);
  // Use a ref for target values so the RAF loop can read them without being a dependency
  const targetBarsRef = useRef<number[]>([0.2, 0.4, 0.6, 0.4, 0.2]);

  // Smooth interpolation using requestAnimationFrame
  useEffect(() => {
    if (!isActive || isPaused) {
      // Reset to idle state when inactive or paused
      setBars([0.2, 0.4, 0.6, 0.4, 0.2]);
      targetBarsRef.current = [0.2, 0.4, 0.6, 0.4, 0.2];
      return;
    }

    let rafId: number;
    const smoothUpdate = () => {
      setBars(prev => prev.map((currentHeight, i) => {
        const target = targetBarsRef.current[i];
        // Smoother interpolation factor (0.15 means 15% towards target each frame)
        const smoothingFactor = 0.15;
        return currentHeight + (target - currentHeight) * smoothingFactor;
      }));
      rafId = requestAnimationFrame(smoothUpdate);
    };

    rafId = requestAnimationFrame(smoothUpdate);
    return () => cancelAnimationFrame(rafId);
  }, [isActive, isPaused]);

  // Update target values based on frequency data
  useEffect(() => {
    if (!isActive || isPaused) {
      return;
    }

    // Set up interval to read frequency data
    const interval = setInterval(() => {
      // If we have frequency data, use it for proper spectrum visualization
      if (frequencyData && frequencyData.length > 0) {
        // Sample rate is 24kHz, so Nyquist frequency is 12kHz
        // With FFT size 2048, we get 1024 frequency bins
        // Each bin represents: 12000Hz / 1024 = ~11.7 Hz per bin
        
        // Voice-optimized frequency ranges (in Hz):
        // Bar 0: 100-300 Hz (fundamental frequencies - bass/male voice)
        // Bar 1: 300-700 Hz (fundamental frequencies - female voice)
        // Bar 2: 700-1500 Hz (lower harmonics - clarity)
        // Bar 3: 1500-2500 Hz (mid harmonics - presence)
        // Bar 4: 2500-4000 Hz (upper harmonics - brightness)
        
        const sampleRate = 24000; // Known from AudioContext
        const nyquist = sampleRate / 2; // 12000 Hz
        const hzPerBin = nyquist / frequencyData.length; // ~11.7 Hz per bin
        
        // Define voice-optimized frequency ranges for each bar
        const voiceRanges = [
          { min: 100, max: 300 },   // Bar 0: Fundamental (bass/male)
          { min: 300, max: 700 },   // Bar 1: Fundamental (female)
          { min: 700, max: 1500 },  // Bar 2: Lower harmonics
          { min: 1500, max: 2500 }, // Bar 3: Mid harmonics
          { min: 2500, max: 4000 }  // Bar 4: Upper harmonics
        ];
        
        // Lower noise threshold for voice-specific frequencies
        const NOISE_THRESHOLD = 30; // Out of 255
        
        const newBars = voiceRanges.map((range, i) => {
          // Calculate bin indices for this frequency range
          const startBin = Math.floor(range.min / hzPerBin);
          const endBin = Math.ceil(range.max / hzPerBin);
          const binSize = endBin - startBin;
          
          // Get average amplitude for this frequency range
          let sum = 0;
          for (let j = startBin; j < endBin && j < frequencyData.length; j++) {
            sum += frequencyData[j];
          }
          const average = sum / binSize;
          
          // Apply noise threshold - below this, stay at minimum
          if (average < NOISE_THRESHOLD) {
            return 0.2;
          }
          
          // Above threshold: subtract threshold and scale
          const adjusted = average - NOISE_THRESHOLD;
          // Use higher multiplier for voice frequencies (they tend to be quieter than broadband noise)
          const normalized = 0.2 + (adjusted / (255 - NOISE_THRESHOLD)) * 4.0;
          return Math.min(1, Math.max(0.2, normalized));
        });
        
        targetBarsRef.current = newBars;
      } else {
        // Fallback to simple audio level visualization
        // Increased multiplier from 25 to 200 for better visibility at normal speaking volume
        // Normal speech levels are around 0.0007-0.0012, so:
        // 0.001 * 200 = 0.2 (20% height - visible movement)
        const scaledLevel = Math.min(1, audioLevel * 200);
        
        targetBarsRef.current = targetBarsRef.current.map((prevHeight) => {
          // Add randomness for variation
          const randomVariation = Math.random() * 0.2;
          const height = scaledLevel * 0.7 + randomVariation * 0.3;
          
          return Math.min(1, Math.max(0.15, height));
        });
      }
    }, 1000 / 30); // 30 FPS update rate

    return () => clearInterval(interval);
  }, [isActive, isPaused, audioLevel, frequencyData]);

  // When paused, show 5 static dots instead of bars
  if (isPaused) {
    return (
      <div 
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: size * 0.15,
          width: size,
          height: size,
        }}
      >
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              width: size * 0.1,
              height: size * 0.1,
              backgroundColor: color,
              borderRadius: '50%',
              opacity: 0.8,
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div 
      style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        gap: size * 0.1,
        width: size,
        height: size,
      }}
    >
      {bars.map((height, i) => (
        <div
          key={i}
          style={{
            width: size * 0.12,
            height: `${height * 100}%`,
            backgroundColor: color,
            borderRadius: size * 0.06,
            // Removed CSS transition - using RAF interpolation for smoother animation
            opacity: isActive ? 1 : 0.5,
          }}
        />
      ))}
    </div>
  );
};

/**
 * Alternative: SVG-based waveform (smoother animations)
 */
export const AudioWaveformSVG: React.FC<AudioWaveformProps> = ({ 
  isActive, 
  audioLevel = 0,
  size = 24,
  color = 'currentColor'
}) => {
  const [displayLevel, setDisplayLevel] = useState(0.2);

  useEffect(() => {
    if (!isActive) {
      setDisplayLevel(0.2);
      return;
    }

    // Directly use the audio level with scaling
    const scaledLevel = Math.min(1, audioLevel * 5);
    setDisplayLevel(Math.max(0.2, scaledLevel));
  }, [isActive, audioLevel]);

  const points = 20;
  const pathData = Array.from({ length: points }, (_, i) => {
    const x = (i / (points - 1)) * size;
    // Simple sine wave that scales with actual audio level
    const y = size / 2 + Math.sin(i * 0.5) * (size / 4) * displayLevel;
    return `${i === 0 ? 'M' : 'L'} ${x},${y}`;
  }).join(' ');

  return (
    <svg 
      width={size} 
      height={size} 
      viewBox={`0 0 ${size} ${size}`}
      style={{ display: 'block' }}
    >
      <path
        d={pathData}
        stroke={color}
        strokeWidth={size * 0.08}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          opacity: isActive ? 1 : 0.5,
          transition: 'opacity 0.2s ease',
        }}
      />
    </svg>
  );
};

/**
 * Simple pulsing circle (minimal)
 */
export const AudioPulse: React.FC<AudioWaveformProps> = ({ 
  isActive, 
  audioLevel = 0,
  size = 24,
  color = 'currentColor'
}) => {
  // Scale audio level for better visibility
  const scaledLevel = Math.min(1, audioLevel * 5);
  const scale = isActive ? 1 + scaledLevel * 0.3 : 1;

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: `${size * 0.1}px solid ${color}`,
        transform: `scale(${scale})`,
        transition: 'transform 0.05s ease-out',
        opacity: isActive ? 1 : 0.5,
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          backgroundColor: color,
          opacity: isActive ? scaledLevel * 0.6 : 0.2,
          transition: 'opacity 0.05s ease-out',
        }}
      />
    </div>
  );
};

export default AudioWaveform;
